import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type VisitorLotteryEventDTO = {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "open" | "closed";
  opensAt: string | null;
  closesAt: string | null;
  successMessageLao: string | null;
  missMessageLao: string | null;
};

export type VisitorLotteryEventStatsDTO = {
  totalRegistrations: number;
  prizeWins: number;
  consolations: number;
  noPrize: number;
  pendingRedeem: number;
  redeemed: number;
  prizeRemainingTotal: number;
};

export type VisitorLotteryPrizeDTO = {
  id: string;
  eventId: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  quantity: number;
  remaining: number;
  claimed?: number;
  probabilityWeight: number;
  isConsolation: boolean;
  drawMode: "instant" | "deferred";
  redeemCodePrefix: string | null;
  enabled: boolean;
  sortOrder: number;
};

export type VisitorRedeemLookupDTO = {
  id: string;
  eventId: string;
  eventTitle: string;
  eventSlug: string;
  fullName: string;
  nickname: string | null;
  phoneNumber: string;
  prizeName: string | null;
  redeemCode: string | null;
  redeemedAt: string | null;
  submittedAt: string;
};

export type VisitorLotteryReportDTO = {
  event: VisitorLotteryEventDTO;
  stats: VisitorLotteryEventStatsDTO;
  prizes: Array<{
    id: string;
    name: string;
    drawMode: string;
    isConsolation: boolean;
    quantity: number;
    remaining: number;
    claimed: number;
    winCount: number;
    enabled: boolean;
  }>;
  winners: Array<{
    id: string;
    fullName: string;
    nickname: string | null;
    phoneNumber: string;
    prizeNameSnapshot: string | null;
    redeemCode: string | null;
    redeemedAt: string | null;
    submittedAt: string;
    drawnAt: string;
  }>;
  deferredDraws: Array<{
    id: string;
    registrationId: string;
    wonPrizeId: string | null;
    rolledValue: number | null;
    createdAt: string;
  }>;
};

export type VisitorRegistrationListItemDTO = {
  id: string;
  eventId: string;
  fullName: string;
  nickname: string | null;
  phoneNumber: string;
  prizeId: string | null;
  prizeName: string | null;
  prizeIsConsolation: boolean;
  redeemCode: string | null;
  redeemedAt: string | null;
  drawnAt: string;
  submittedAt: string;
  adminNotes: string | null;
};

export const visitorLotteryApi = {
  listEvents: () =>
    fetcher.get<{ items: VisitorLotteryEventDTO[] }>(
      `${config.apiUrl}/visitor-lottery/events`,
    ),

  initializeSummerVisitor2026: () =>
    fetcher.post<{
      eventId: string;
      slug: string;
      eventCreated: boolean;
      prizesCreated: number;
      prizeCount: number;
    }>(
      `${config.apiUrl}/visitor-lottery/events/initialize-summer-visitor-2026`,
    ),

  getEvent: (id: string) =>
    fetcher.get<
      VisitorLotteryEventDTO & {
        prizes: VisitorLotteryPrizeDTO[];
        stats: VisitorLotteryEventStatsDTO;
      }
    >(`${config.apiUrl}/visitor-lottery/events/${id}`),

  updateEvent: (
    id: string,
    body: Partial<
      Pick<
        VisitorLotteryEventDTO,
        | "title"
        | "status"
        | "opensAt"
        | "closesAt"
        | "successMessageLao"
        | "missMessageLao"
      >
    >,
  ) =>
    fetcher.put<VisitorLotteryEventDTO>(
      `${config.apiUrl}/visitor-lottery/events/${id}`,
      body,
    ),

  createPrize: (
    eventId: string,
    body: {
      name: string;
      description?: string | null;
      imageUrl?: string | null;
      quantity: number;
      probabilityWeight: number;
      isConsolation?: boolean;
      drawMode?: "instant" | "deferred";
      redeemCodePrefix?: string | null;
      enabled?: boolean;
      sortOrder?: number;
    },
  ) =>
    fetcher.post<VisitorLotteryPrizeDTO>(
      `${config.apiUrl}/visitor-lottery/events/${eventId}/prizes`,
      body,
    ),

  updatePrize: (
    eventId: string,
    prizeId: string,
    body: Partial<{
      name: string;
      description: string | null;
      imageUrl: string | null;
      quantity: number;
      probabilityWeight: number;
      isConsolation: boolean;
      drawMode: "instant" | "deferred";
      redeemCodePrefix: string | null;
      enabled: boolean;
      sortOrder: number;
    }>,
  ) =>
    fetcher.patch<VisitorLotteryPrizeDTO>(
      `${config.apiUrl}/visitor-lottery/events/${eventId}/prizes/${prizeId}`,
      body,
    ),

  deletePrize: (eventId: string, prizeId: string) =>
    fetcher.delete<{ ok: true }>(
      `${config.apiUrl}/visitor-lottery/events/${eventId}/prizes/${prizeId}`,
    ),

  listRegistrations: (
    query: OffsetPageQueryDTO & {
      eventId?: string;
      prizeId?: string;
      redeemed?: string;
      won?: string;
      search?: string;
    },
  ) => {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.eventId) params.set("eventId", query.eventId);
    if (query.prizeId) params.set("prizeId", query.prizeId);
    if (query.redeemed) params.set("redeemed", query.redeemed);
    if (query.won) params.set("won", query.won);
    if (query.search) params.set("search", query.search);
    if (query.sort?.length) {
      params.set("sort", JSON.stringify(query.sort));
    }
    return fetcher.get<{
      data: VisitorRegistrationListItemDTO[];
      meta: { total: number; limit: number; offset: number };
    }>(`${config.apiUrl}/visitor-lottery/registrations?${params.toString()}`);
  },

  redeemRegistration: (id: string) =>
    fetcher.post<{
      registration: VisitorRegistrationListItemDTO;
      alreadyRedeemed: boolean;
    }>(`${config.apiUrl}/visitor-lottery/registrations/${id}/redeem`),

  deleteRegistration: (id: string) =>
    fetcher.delete<{
      ok: true;
      stockRestored: boolean;
      restoredPrizeId: string | null;
      registration: {
        id: string;
        eventId: string;
        fullName: string;
        phoneNumber: string;
        prizeId: string | null;
        prizeNameSnapshot: string | null;
        redeemCode: string | null;
      };
    }>(`${config.apiUrl}/visitor-lottery/registrations/${id}`),

  lookupRedeemCode: (code: string) =>
    fetcher.get<VisitorRedeemLookupDTO>(
      `${config.apiUrl}/visitor-lottery/redeem/lookup?code=${encodeURIComponent(code)}`,
    ),

  redeemByCode: (code: string) =>
    fetcher.post<{
      registration: VisitorRedeemLookupDTO;
      alreadyRedeemed: boolean;
    }>(`${config.apiUrl}/visitor-lottery/redeem/by-code`, { code }),

  drawDeferred: (eventId: string, prizeId: string, count?: number) =>
    fetcher.post<{
      prizeId: string;
      prizeName: string;
      requested: number;
      drawn: number;
      eligiblePool: number;
      winners: Array<{
        registrationId: string;
        fullName: string;
        phoneNumber: string;
        redeemCode: string;
      }>;
    }>(
      `${config.apiUrl}/visitor-lottery/events/${eventId}/prizes/${prizeId}/draw-deferred`,
      { count },
    ),

  getReport: (eventId: string) =>
    fetcher.get<VisitorLotteryReportDTO>(
      `${config.apiUrl}/visitor-lottery/events/${eventId}/report`,
    ),

  exportCsv: async (
    query: {
      eventId: string;
      prizeId?: string;
      redeemed?: string;
      won?: string;
      search?: string;
    },
  ) => {
    const params = new URLSearchParams();
    params.set("eventId", query.eventId);
    if (query.prizeId) params.set("prizeId", query.prizeId);
    if (query.redeemed) params.set("redeemed", query.redeemed);
    if (query.won) params.set("won", query.won);
    if (query.search) params.set("search", query.search);
    const res = await fetch(
      `${config.apiUrl}/visitor-lottery/registrations/export.csv?${params.toString()}`,
      { credentials: "include" },
    );
    if (!res.ok) throw new Error("Export failed");
    return res.blob();
  },

  exportNames: async (
    query: {
      eventId: string;
      prizeId?: string;
      redeemed?: string;
      won?: string;
      search?: string;
    },
  ) => {
    const params = new URLSearchParams();
    params.set("eventId", query.eventId);
    if (query.prizeId) params.set("prizeId", query.prizeId);
    if (query.redeemed) params.set("redeemed", query.redeemed);
    if (query.won) params.set("won", query.won);
    if (query.search) params.set("search", query.search);
    const res = await fetch(
      `${config.apiUrl}/visitor-lottery/registrations/export-names.txt?${params.toString()}`,
      { credentials: "include" },
    );
    if (!res.ok) throw new Error("Export failed");
    return res.blob();
  },
};
